import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { createNoCacheOptions } from '../utils/http-headers.util';

export interface AdminAnalysisPreviewRequest {
  transcriptText: string;
  specialty?: string;
}

export interface AdminAnalysisPreviewResponse {
  transcriptionId?: number;
  analysisId?: string;
  status?: string;
  summary?: string;
  diagnosis?: string;
  transcriptText?: string;
  promptVersion?: string;
  soapNote?: {
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
  };
  aiAnalysis?: {
    keyFindings?: string[];
    treatmentPlanSummary?: string[];
    suggestedICD10Codes?: Array<{ code: string; description: string }>;
    medicalTerms?: Array<{ term: string; definition: string }>;
  };
  medicalCodes?: {
    icd10Codes?: Array<{ text: string; code: string }>;
    rxNormCodes?: Array<{ text: string; code: string }>;
    snomedCodes?: Array<{ text: string; code: string }>;
  };
}

export interface SoapComparisonRequest {
  transcriptionId: string;
  desiredSoapText: string;
}

export interface SoapSectionComparison {
  section: string;
  similarityScore: number;
  missingItems: string[];
  extraItems: string[];
}

export interface SoapComparisonResponse {
  overallSimilarityScore: number;
  sectionComparisons: SoapSectionComparison[];
  generatedSoap?: {
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
  };
  desiredSoap?: {
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
  };
  learnedSummary?: string;
  promptChangeNotes?: string;
  possibleImprovement?: string;
  promptVersion?: string;
  comparisonId?: string;
  savedComparisonPath?: string | null;
}

export interface AdminComparisonSummary {
  comparisonId: string;
  transcriptionId?: number;
  createdAt?: string | null;
  overallSimilarityScore?: number | null;
  promptVersion?: string | null;
  possibleImprovement?: string | null;
}

export interface AdminTranscriptSummary {
  transcriptionId: number;
  createdAt?: string | null;
  status?: string | null;
  hasAnalysis?: boolean;
}

export interface AdminReplayRangeRequest {
  startId: number;
  endId: number;
  analysisVersion?: 'v2' | 'v3';
}

export interface AdminReplayItemResult {
  transcriptionId: number;
  status: string;
  message?: string | null;
  oldAnalysisId?: string | null;
  newAnalysisId?: string | null;
  comparisonId?: string | null;
  overallSimilarityScore?: number | null;
  promptVersion?: string | null;
}

export interface AdminReplayRangeResponse {
  runId: string;
  createdAt?: string | null;
  startId: number;
  endId: number;
  analysisVersion?: string | null;
  requestedCount: number;
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  items: AdminReplayItemResult[];
}

export interface AdminReplayState {
  lastRunId?: string | null;
  lastRunAt?: string | null;
  lastStartId?: number | null;
  lastEndId?: number | null;
  suggestedNextStartId?: number | null;
  availableMinId?: number | null;
  availableMaxId?: number | null;
  availableCount?: number | null;
}

export interface AdminReplayRunSummary {
  runId: string;
  createdAt?: string | null;
  startId?: number | null;
  endId?: number | null;
  processedCount?: number | null;
  skippedCount?: number | null;
  failedCount?: number | null;
}

@Injectable({ providedIn: 'root' })
export class DevAdminService {
  private baseUrl = environment.apiUrl;
  private apiKey = environment.apiKey;

  constructor(private http: HttpClient) {}

  runAnalysisPreview(request: AdminAnalysisPreviewRequest): Observable<AdminAnalysisPreviewResponse> {
    return this.http.post<AdminAnalysisPreviewResponse>(
      `${this.baseUrl}/admin/analysis/preview`,
      request,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  compareSoap(request: SoapComparisonRequest): Observable<SoapComparisonResponse> {
    return this.http.post<SoapComparisonResponse>(
      `${this.baseUrl}/admin/soap/compare`,
      request,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  fetchSavedComparisons(): Observable<AdminComparisonSummary[]> {
    return this.http.get<AdminComparisonSummary[]>(
      `${this.baseUrl}/admin/soap/compare`,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  fetchComparisonById(comparisonId: string): Observable<SoapComparisonResponse> {
    return this.http.get<SoapComparisonResponse>(
      `${this.baseUrl}/admin/soap/compare/${comparisonId}`,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  fetchAnalysisById(transcriptionId: string): Observable<AdminAnalysisPreviewResponse> {
    return this.http.get<AdminAnalysisPreviewResponse>(
      `${this.baseUrl}/admin/analysis/${transcriptionId}`,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  fetchSavedAnalyses(): Observable<AdminTranscriptSummary[]> {
    return this.http.get<AdminTranscriptSummary[]>(
      `${this.baseUrl}/admin/analysis`,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  reanalyze(transcriptionId: string): Observable<AdminAnalysisPreviewResponse> {
    return this.http.post<AdminAnalysisPreviewResponse>(
      `${this.baseUrl}/admin/analysis/${transcriptionId}/reanalyze`,
      {},
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  deleteAnalysis(transcriptionId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/admin/analysis/${transcriptionId}`,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  replaySoapRange(request: AdminReplayRangeRequest): Observable<AdminReplayRangeResponse> {
    return this.http.post<AdminReplayRangeResponse>(
      `${this.baseUrl}/admin/soap/replay`,
      request,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  fetchReplayState(): Observable<AdminReplayState> {
    return this.http.get<AdminReplayState>(
      `${this.baseUrl}/admin/soap/replay/state`,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }

  fetchReplayRuns(): Observable<AdminReplayRunSummary[]> {
    return this.http.get<AdminReplayRunSummary[]>(
      `${this.baseUrl}/admin/soap/replay/runs`,
      createNoCacheOptions({ 'api-key': this.apiKey })
    );
  }
}
